// Shared "does this card currently have <keyword>?" predicate for the
// core/ engine modules. Pure. No React, no Three.js, no reach into
// automation/.
//
// Single source of truth that respects two deny lists:
//   • `_suppressedKeywords`. Player's manual radial-menu deny.
//   • `_auraSuppressedKeywords`. Aura-driven (REVOKE_KEYWORD), rebuilt
//                                  every static-aura recompute so
//                                  source-leave restores the keyword.
//
// Without these, each engine module's inline hasStatus helper would
// re-merge a printed keyword back in after suppression, leaving the
// badge faded but combat / movement / sickness still treating the
// keyword as active.
//
// Order of precedence matches getEffectiveStatuses:
//   1. Manual statuses always count (player explicitly added).
//   2. Aura + printed grants count UNLESS suppressed (either deny list).

export function hasKeyword(card, key) {
  if (!card) return false;
  if ((card.statuses || []).includes(key)) return true;
  if ((card._suppressedKeywords     || []).includes(key)) return false;
  if ((card._auraSuppressedKeywords || []).includes(key)) return false;
  // Silence (Sorcery): "Minions occupying affected sites are silenced."
  // A silenced card loses ALL box text. That includes every printed /
  // aura-granted keyword (airborne, stealth, spellcaster, charge, …).
  // The basic abilities (move, attack, defend, intercept) are inherent
  // to every minion and aren't keywords, so they remain available. We
  // keep the 'silenced' keyword itself reportable so the badge / UI can
  // still render it.
  // 'flooded' is exempt: it denotes the site's water-site characteristic,
  // and the only effect that silences SITES (Atlantean Fate: "they are
  // water sites ... and lose all OTHER abilities") co-grants flooded in
  // the same breath, so it must outlive its own silence.
  // ponytail: flooded is silence-immune everywhere; a printed-flooded
  // site silenced by some OTHER future site-silencer would wrongly keep
  // its water. No such card exists today; revisit if one ships.
  // 'disabled' is exempt too: codex Disabled "is not an ability itself and
  // is not removed by silenced effects", so a silenced unit standing in a
  // disabling aura stays disabled. isDisabled routes through here.
  if (key !== 'silenced' && key !== 'flooded' && key !== 'disabled' && isCardSilenced(card)) return false;
  if ((card._auraStatuses    || []).includes(key)) return true;
  if ((card._printedKeywords || []).includes(key)) return true;
  return false;
}

/**
 * Is this card currently silenced? A silenced card is treated as if its
 * printed and aura-granted box text doesn't exist. No keywords, no
 * triggered/static/activated abilities. The flag itself is delivered
 * through `_auraStatuses` (by Silence's GRANT_KEYWORD) or `statuses`
 * (manual radial-menu toggle). Suppression of the 'silenced' keyword
 * itself still wins so a `_suppressedKeywords: ['silenced']` toggle
 * fully restores the card.
 */
export function isCardSilenced(card) {
  if (!card) return false;
  if ((card._suppressedKeywords     || []).includes('silenced')) return false;
  if ((card._auraSuppressedKeywords || []).includes('silenced')) return false;
  if ((card.statuses        || []).includes('silenced')) return true;
  if ((card._auraStatuses   || []).includes('silenced')) return true;
  if ((card._printedKeywords || []).includes('silenced')) return true;
  return false;
}
